// ./src/utils/api/AppFeatureUsageService.ts
//
// How much of each METERED allowance this account has consumed in a period — the read behind
// "7 of 250 emails used" on the Subscription page.
//
// ⚠ This op lives in ms_auth, not eventsmagic-api, and it is `doAuth: true` and scoped to the
// CALLER's own account from the token. It takes no accountId, so there is nothing to spoof.
// (Its interservice sibling `accountFeatureUsageWindow` DOES take an accountId and is gated
// only on the interservice key — never call that one from a browser.)
//
// ⚠ TWO THINGS THE RESPONSE DOES NOT SAY, both of which have to be handled by the caller:
//
//   1. A feature with no consumption this period has NO ROW AT ALL — it is absent, not zero.
//      Measured on 2026-09-10: an account on `free` that had sent 7 emails and no messages
//      returned exactly ONE row (`email_qty`), with `message_qty` and `ai_qty` missing
//      entirely. Rendering only what comes back would silently drop two of the three
//      allowances, and a host who has sent nothing would see an empty list — which reads as
//      "you have no allowance" rather than "you have used none of it".
//   2. `limitValue` / `remaining` carry ms_auth's OWN sentinel convention (`-1` = unlimited),
//      and `limitValue` falls back to `0` when it cannot find a plan row. This app reads `0`
//      through the per-feature `zeroMeans` declaration instead. DO NOT render the limit from
//      here — take `usageCount` and read the limit from the plan, which is the same authority
//      the entitlement gate uses. See `useAppFeatureUsage`.
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";

const APP_FEATURE_USAGE_SUMMARY_QUERY = gql`
  query AppFeatureUsageSummary($periodKey: String) {
    appFeatureUsageSummary(periodKey: $periodKey) {
      code
      data {
        featureCode
        featureName
        usageCount
        limitValue
        periodKey
        remaining
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

export interface AppFeatureUsageSummaryRow {
  featureCode?: string | null;
  featureName?: string | null;
  usageCount?: number | null;
  /** ⚠ ms_auth's own limit, with ms_auth's sentinels. Read the limit from the plan instead. */
  limitValue?: number | null;
  periodKey?: string | null;
  /** ⚠ Same caveat as `limitValue` — `-1` means unlimited here. */
  remaining?: number | null;
}

export class AppFeatureUsageService {
  /**
   * Usage for one period. Omit `periodKey` for the current UTC month, which is what the
   * server defaults to (`YYYY-MM`).
   */
  static async summary(periodKey?: string | null): Promise<OpResult> {
    return ApiService.executeQuery(APP_FEATURE_USAGE_SUMMARY_QUERY, {
      periodKey: periodKey || undefined,
    });
  }
}
